/**
 * The words the planner uses, kept away from the components that render them.
 *
 * Two reasons this is its own file. It is the whole of the accessibility requirement -
 * every state a player has to distinguish is a *word* here, and colour is decoration
 * applied on top - so it has to be readable in one place to check that none of them is
 * only a colour. And it is pure, so the mapping can be tested without mounting anything.
 */

/**
 * Why a planned pick can no longer be followed, said plainly.
 *
 * The server's PlanValidity, one for one. An unknown value falls back to a real sentence
 * rather than to the enum constant: a release that adds a reason should degrade to
 * "something changed", not print FIXTURE_REARRANGED at somebody.
 */
const VALIDITY_TEXT = {
  VALID: null,
  TEAM_ALREADY_USED: 'Already used',
  FIXTURE_CHANGED: 'Fixture changed',
  FIXTURE_VOIDED: 'Fixture called off',
  DUPLICATE_TEAM: 'Planned twice',
  WEEK_UNAVAILABLE: 'Gameweek changed',
  SUPERSEDED: 'You have picked this week',
};

/** Longer form, for the line under the route chip where there is room to explain. */
const VALIDITY_DETAIL = {
  TEAM_ALREADY_USED: 'You have since used this team, so you cannot pick it again.',
  FIXTURE_CHANGED: 'This team no longer has a fixture in this gameweek.',
  FIXTURE_VOIDED: 'This fixture has been called off, so nobody can be picked from it.',
  DUPLICATE_TEAM: 'You have this team planned for an earlier gameweek too.',
  WEEK_UNAVAILABLE: 'This gameweek no longer has fixtures.',
  SUPERSEDED: 'Your pick for this gameweek is already confirmed.',
};

export function isPlanValid(planned) {
  return !planned || planned.validity === 'VALID';
}

export function validityLabel(validity) {
  if (!validity || validity === 'VALID') return null;
  return VALIDITY_TEXT[validity] ?? 'Needs replanning';
}

export function validityDetail(validity) {
  if (!validity || validity === 'VALID') return null;
  return VALIDITY_DETAIL[validity] ?? 'Something about this plan has changed. Pick again.';
}

/**
 * What one team means to this player in this week, as text.
 *
 * The three availability states get three different sentences rather than three shades
 * of one, because a planner read on a phone in a hurry is exactly where "greyed out" and
 * "slightly greyed out" stop being distinguishable. `PLANNED_ANOTHER_WEEK` names the week
 * holding the team, since "unavailable" without the reason is the thing that sends a
 * player hunting back through five cards.
 */
export function availabilityLabel(team) {
  if (!team) return '';
  switch (team.availability) {
    case 'USED':
      return 'Already used';
    case 'PLANNED_THIS_WEEK':
      return 'Your plan';
    case 'PLANNED_ANOTHER_WEEK':
      return `Planned for GW${team.plannedForGameWeek}`;
    default:
      return 'Available';
  }
}

/**
 * The confirmed pick, which is not one of the availabilities above and must not be
 * rendered as one.
 *
 * <p>A team you have already picked this week comes back as `USED` - correctly, since it
 * is spent - and "Already used" alongside every other team you have burned is the one
 * label that hides the fact the player came to the current week to check. So the pick is
 * named separately wherever the week has one, and the availability underneath it is
 * exactly what it was.
 */
export const CONFIRMED_LABEL = 'Your pick';
export const CONFIRMED_MARK = '✔';

/** The symbol beside the label. Never the only cue - it always sits next to the words. */
export function availabilityMark(team) {
  if (!team) return '';
  switch (team.availability) {
    case 'USED':
      return '✕';
    case 'PLANNED_THIS_WEEK':
      return '★';
    case 'PLANNED_ANOTHER_WEEK':
      return '◷';
    default:
      return '';
  }
}

/**
 * "Sat 14 Sep" and "Sat 14 Sep, 15:00" - or a date range when a week spans days.
 *
 * A gameweek is normally a weekend rather than a day, and printing only the first
 * kick-off makes a Sunday fixture look like it is on Saturday.
 */
export function formatKickOff(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function formatDateRange(first, last) {
  if (!first) return '';
  const start = new Date(first);
  const end = last ? new Date(last) : start;
  if (Number.isNaN(start.getTime())) return '';
  const opts = { weekday: 'short', day: 'numeric', month: 'short' };
  const startText = start.toLocaleDateString('en-GB', opts);
  const endText = Number.isNaN(end.getTime()) ? startText : end.toLocaleDateString('en-GB', opts);
  return startText === endText ? startText : `${startText} – ${endText}`;
}

/** How a gameweek's own status reads to a player, when it is worth saying at all. */
export function weekStatusLabel(week) {
  if (!week) return '';
  switch (week.weekStatus) {
    case 'LOCKED': return 'Picks locked';
    case 'CLOSED': return 'Gameweek closed';
    case 'VOIDED': return 'Gameweek voided';
    case 'ACTIVE': return 'Picks open';
    case 'UPCOMING': return 'Not open yet';
    default: return '';
  }
}
